import React, { useState, useEffect, useCallback } from 'react';
import { foundationAPI, jobApiError } from '../services/api';
import { FiAlertCircle, FiAlertTriangle, FiCheckCircle, FiMinusCircle, FiRefreshCw, FiXCircle } from 'react-icons/fi';

// Foundation step 2 (brainstorms/2026-10-17-foundation-enforcement.md, Q6/Q9):
// KPI dashboard, gap analysis (self, assessor, one-to-one, signed plan), conduct records.

const muted = { color: '#64748b', fontSize: 12 };
const row = { padding: '8px 0', borderBottom: '1px solid #e2e8f0', fontSize: 13 };
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const Err = ({ text }) => (text ? <div className="error-message" style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '8px 0' }}><FiAlertCircle size={16} />{text}</div> : null);
const Ok = ({ text }) => (text ? <div style={{ color: '#15803d', fontSize: 13, margin: '8px 0', display: 'flex', gap: 6, alignItems: 'center' }}><FiCheckCircle />{text}</div> : null);

// Colour with an icon and a word on it, so it never relies on colour alone.
// The colours are the shared status tokens in colour-tokens.css.
const COLOURS = {
  GREEN: ['var(--kpi-on)', 'Green', FiCheckCircle],
  AMBER: ['var(--kpi-risk)', 'Amber', FiAlertTriangle],
  RED: ['var(--kpi-off)', 'Red', FiXCircle],
  GREY: ['var(--kpi-none)', 'No colour yet', FiMinusCircle],
};
export const Dot = ({ colour }) => {
  const [hex, label, Icon] = COLOURS[colour] || COLOURS.GREY;
  return (
    <span className="status-pill" style={{ color: hex, minWidth: 84, display: 'inline-flex', gap: 4, alignItems: 'center', justifyContent: 'center' }}>
      <Icon size={11} aria-hidden="true" />{label}
    </span>
  );
};

const thisMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };
export const UNIT = { PERCENT: '%', COUNT: '', DAYS: ' days', SCORE: ' / 5', MONTHS: ' months', MONEY: '' };
const GROUPS = [['CH', 'Chairman'], ['CEO', 'Chief Executive Officer'], ['COO', 'Chief Operating Officer'], ['CFO', 'Chief Financial Officer'],
  ['CMO', 'Chief Marketing Officer'], ['CSM', 'Chief Sales Manager'], ['CSO', 'Customer Support Officers'], ['COMPANY', 'Shared company KPIs'],
  ['PERSON', 'Values and team-rules KPIs (every person)']];

// ─── KPIs ──────────────────────────────────────────────────────────────────
const EntryForm = ({ kpi, period, person, onSaved }) => {
  const [value, setValue] = useState('');
  const [evidence, setEvidence] = useState('');
  const [err, setErr] = useState('');
  const save = async () => {
    try { await foundationAPI.kpiReading(kpi.id, { period, value, evidence, person }); setValue(''); setEvidence(''); setErr(''); onSaved(); } catch (e) { setErr(jobApiError(e)); }
  };
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
      <input style={{ width: 90 }} type="number" step="any" placeholder="Value" value={value} onChange={(e) => setValue(e.target.value)} />
      <input style={{ flex: 1, minWidth: 180 }} placeholder="Where the number comes from" value={evidence} onChange={(e) => setEvidence(e.target.value)} />
      <button className="btn-secondary" disabled={value === '' || !evidence.trim()} onClick={save}>Save</button>
      {err && <span style={{ color: '#b91c1c', fontSize: 12 }}>{err}</span>}
    </div>
  );
};

const TargetForm = ({ kpi, onSaved }) => {
  const [f, setF] = useState({ kind: 'NUMBER', value: '', effective_from: '' });
  const [err, setErr] = useState('');
  const save = async () => {
    try { await foundationAPI.kpiTarget(kpi.id, f); setErr(''); onSaved(); } catch (e) { setErr(jobApiError(e)); }
  };
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
      <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}>
        <option value="NUMBER">Number</option><option value="ZERO">Zero</option><option value="TREND">Better than last month</option><option value="NONE">Not set</option>
      </select>
      {f.kind === 'NUMBER' && <input style={{ width: 90 }} type="number" step="any" placeholder="Target" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} />}
      <label style={muted}>from <input type="date" value={f.effective_from} onChange={(e) => setF({ ...f, effective_from: e.target.value })} /></label>
      <button className="btn-secondary" onClick={save}>Set target</button>
      {err && <span style={{ color: '#b91c1c', fontSize: 12 }}>{err}</span>}
    </div>
  );
};

const KpiCard = ({ kpi, period, canSetTargets, onChanged }) => {
  const [open, setOpen] = useState(false);
  const targetLabel = kpi.target ? (kpi.target.kind === 'NUMBER' ? `${kpi.target.value}${UNIT[kpi.unit]}` : kpi.target.kind.toLowerCase()) : 'not in force yet';
  return (
    <div style={row}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer' }} onClick={() => setOpen(!open)}>
        <strong style={{ minWidth: 48 }}>{kpi.code}</strong>
        <span style={{ flex: 1 }}>{kpi.name}<span style={muted}> · target {kpi.target_text} · {kpi.frequency}{kpi.mode === 'AUTO' ? ' · calculated by the VMS' : ''}</span></span>
        {kpi.scope !== 'PERSON' && <Dot colour={kpi.rows[0]?.colour} />}
        {kpi.scope !== 'PERSON' && kpi.rows[0]?.value != null && <span style={{ minWidth: 70, textAlign: 'right' }}>{Number(kpi.rows[0].value)}{UNIT[kpi.unit]}</span>}
      </div>
      {(open || kpi.scope === 'PERSON') && (
        <div style={{ marginLeft: 56, marginTop: 6 }}>
          {kpi.scope === 'PERSON' && kpi.rows.map((r) => (
            <div key={r.person_id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '2px 0' }}>
              <span style={{ flex: 1 }}>{r.person}</span>
              {r.value != null && <span>{Number(r.value)}{UNIT[kpi.unit]}</span>}
              <Dot colour={r.colour} />
              {r.can_enter && open && <EntryForm kpi={kpi} period={period} person={r.person_id} onSaved={onChanged} />}
            </div>
          ))}
          {open && (
            <>
              <div style={muted}>How it is measured: {kpi.how_measured}. Target in force: {targetLabel}.{kpi.baseline_first ? ' Baseline quarter: shown grey until the 9 Jan 2027 review.' : ''}</div>
              {kpi.scope !== 'PERSON' && kpi.rows[0]?.evidence && <div style={muted}>Evidence: {kpi.rows[0].evidence}</div>}
              {kpi.scope !== 'PERSON' && kpi.rows[0]?.can_enter && <EntryForm kpi={kpi} period={period} person={null} onSaved={onChanged} />}
              {kpi.mode === 'MANUAL' && !kpi.rows.some((r) => r.can_enter) && <div style={muted}>Entered monthly by the {kpi.entry_role || 'assessor'}.</div>}
              {canSetTargets && <TargetForm kpi={kpi} onSaved={onChanged} />}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export const KpiTab = ({ canRefresh }) => {
  const [period, setPeriod] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const load = useCallback(() => foundationAPI.kpis(period).then((r) => { setData(r.data); setErr(''); }).catch((e) => setErr(jobApiError(e))), [period]);
  useEffect(() => { load(); }, [load]);
  const refresh = async () => { try { await foundationAPI.kpiRefresh(period); load(); } catch (e) { setErr(jobApiError(e)); } };
  const group = (key) => (data?.kpis || []).filter((k) => (key === 'COMPANY' || key === 'PERSON' ? k.scope === key : k.role === key && k.scope === 'ROLE'));
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
        <label style={muted}>Month <input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} /></label>
        {canRefresh && <button className="btn-secondary" onClick={refresh}><FiRefreshCw /> Recalculate automatic KPIs</button>}
        <span style={muted}>Green = target met · Amber = up to 20% short · Red = more than 20% short. A Red KPI starts a support conversation, never a disciplinary one.</span>
      </div>
      <Err text={err} />
      {data && GROUPS.map(([key, title]) => {
        const items = group(key);
        if (!items.length) return null;
        return (
          <div key={key} className="cv-subsection">
            <div className="cv-subsection-title"><span>{title}</span></div>
            {items.map((k) => <KpiCard key={k.id} kpi={k} period={period} canSetTargets={data.can_set_targets} onChanged={load} />)}
          </div>
        );
      })}
    </div>
  );
};

// ─── Gap analysis ──────────────────────────────────────────────────────────
// Part 4: self-assessment → assessor scoring → one-to-one (agreed scores) →
// development plan signed by both → progress followed monthly.
const SCALE = [[1, 'Not yet'], [2, 'Developing'], [3, 'Competent'], [4, 'Strong']];
const ACTION_KINDS = [['TRAIN', 'Train'], ['COACH', 'Coach'], ['CONSULT', 'Consult']];
const ACTION_STATUSES = [['PLANNED', 'Planned'], ['IN_PROGRESS', 'In progress'], ['DONE', 'Done'], ['DROPPED', 'Dropped']];
const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

const ScoreSelect = ({ value, onChange, placeholder }) => (
  <select value={value || ''} onChange={(e) => onChange(e.target.value)}>
    <option value="">{placeholder}</option>{SCALE.map(([v, l]) => <option key={v} value={v}>{v} {l}</option>)}
  </select>
);

const ActionRow = ({ a, sheet, onChanged, setErr }) => {
  const [status, setStatus] = useState(a.status);
  const [note, setNote] = useState(a.progress_note || '');
  const run = async (fn) => { try { const r = await fn(); onChanged(r.data); setErr(''); } catch (e) { setErr(jobApiError(e)); } };
  return (
    <div style={{ ...row, display: 'grid', gridTemplateColumns: '1.2fr 70px 1.6fr 1fr 90px 1fr', gap: 8, alignItems: 'start' }}>
      <div>{a.item || <span style={muted}>General</span>}{a.gap != null && <div style={muted}>Gap {a.gap}</div>}</div>
      <div>{a.kind_label}</div>
      <div>{a.action}{a.support && <div style={muted}>Support: {a.support}</div>}</div>
      <div>{a.owner_name || '—'}</div>
      <div>{fmtDate(a.target_date)}</div>
      <div>
        {sheet.can_follow_up ? (
          <>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>{ACTION_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
            <input style={{ width: '100%', marginTop: 4 }} placeholder="Progress" value={note} onChange={(e) => setNote(e.target.value)} />
            <button className="btn-secondary" style={{ marginTop: 4 }} onClick={() => run(() => foundationAPI.updateGapAction(a.id, { status, progress_note: note }))}>Update</button>
          </>
        ) : (
          <>
            {a.status_label}{a.progress_note && <div style={muted}>{a.progress_note}</div>}
            {sheet.can_edit_plan && <button className="btn-secondary" style={{ marginTop: 4 }} onClick={() => window.confirm('Remove this action?') && run(() => foundationAPI.deleteGapAction(a.id))}>Remove</button>}
          </>
        )}
      </div>
    </div>
  );
};

const PlanSection = ({ sheet, people, onChanged }) => {
  const blank = { score: '', kind: 'TRAIN', action: '', owner: sheet.person_id, support: '', target_date: '' };
  const [f, setF] = useState(blank);
  const [sig, setSig] = useState({ typed_name: '', agree: false });
  const [err, setErr] = useState('');
  const gaps = sheet.scores.filter((s) => (s.gap || 0) > 0);
  const covered = new Set(sheet.actions.filter((a) => a.status !== 'DROPPED').map((a) => a.score));
  const uncovered = gaps.filter((s) => !covered.has(s.id));
  const add = async () => {
    try { const r = await foundationAPI.addGapAction(sheet.id, { ...f, score: f.score || null }); onChanged(r.data); setF(blank); setErr(''); } catch (e) { setErr(jobApiError(e)); }
  };
  const sign = async () => {
    try { const r = await foundationAPI.saveGapReview(sheet.id, { op: 'sign', ...sig }); onChanged(r.data); setErr(''); } catch (e) { setErr(jobApiError(e)); }
  };
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontWeight: 600 }}>Development plan</div>
      <div style={muted}>For each gap, agree an action (train first, then coach, then consult), an owner and a date. Plans due {fmtDate(sheet.plan_due)}.</div>
      <Err text={err} />
      {sheet.actions.length === 0 && <div className="cv-subsection-empty">No actions yet.</div>}
      {sheet.actions.map((a) => <ActionRow key={a.id} a={a} sheet={sheet} onChanged={onChanged} setErr={setErr} />)}
      {sheet.can_edit_plan && (
        <div style={{ marginTop: 8 }}>
          {uncovered.length > 0 && <div style={{ color: '#b45309', fontSize: 13 }}>{uncovered.length} gap(s) still need an action before the plan can be signed.</div>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 6, marginTop: 6 }}>
            <select value={f.score} onChange={(e) => setF({ ...f, score: e.target.value })}>
              <option value="">Gap item…</option>{gaps.map((s) => <option key={s.id} value={s.id}>{covered.has(s.id) ? '' : '• '}{s.text} (gap {s.gap})</option>)}
            </select>
            <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}>{ACTION_KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
            <select value={f.owner} onChange={(e) => setF({ ...f, owner: e.target.value })}>
              {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <label style={muted}>Target date<input type="date" value={f.target_date} onChange={(e) => setF({ ...f, target_date: e.target.value })} /></label>
          </div>
          <input style={{ width: '100%', marginTop: 6 }} placeholder="Action (e.g. a course, shadowing a colleague, a guide)" value={f.action} onChange={(e) => setF({ ...f, action: e.target.value })} />
          <input style={{ width: '100%', marginTop: 6 }} placeholder="Support needed: cost, time (optional)" value={f.support} onChange={(e) => setF({ ...f, support: e.target.value })} />
          <div style={{ textAlign: 'right', marginTop: 6 }}><button className="btn-secondary" disabled={!f.action.trim() || !f.target_date} onClick={add}>Add action</button></div>
        </div>
      )}
      <div style={{ ...muted, marginTop: 10 }}>
        Signed (person): {sheet.person_signed_at ? `${sheet.person_signed_name}, ${fmtDate(sheet.person_signed_at)}` : 'not yet'} ·
        Signed (assessor): {sheet.assessor_signed_at ? `${sheet.assessor_signed_name}, ${fmtDate(sheet.assessor_signed_at)}` : 'not yet'}
        {sheet.status === 'PLAN' && ' · Changing the plan after one signature clears both, so you both sign the same plan.'}
      </div>
      {sheet.can_sign && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }}>
          <label style={{ fontSize: 13 }}><input type="checkbox" checked={sig.agree} onChange={(e) => setSig({ ...sig, agree: e.target.checked })} /> I agree to this development plan</label>
          <input placeholder="Type your full name" value={sig.typed_name} onChange={(e) => setSig({ ...sig, typed_name: e.target.value })} />
          <button className="btn-primary" disabled={!sig.agree || sig.typed_name.trim().split(/\s+/).length < 2} onClick={sign}>Sign plan</button>
        </div>
      )}
    </div>
  );
};

const Sheet = ({ id, people, onClose, onChanged }) => {
  const [sheet, setSheet] = useState(null);
  const [scores, setScores] = useState({});
  const [meeting, setMeeting] = useState({ held_on: today(), notes: '' });
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');
  const apply = (data) => {
    setSheet(data);
    setScores(Object.fromEntries(data.scores.map((s) => [s.item_id, {
      self_score: s.self_score || '', self_note: s.self_note || '', assessor_score: s.assessor_score || '',
      assessor_note: s.assessor_note || '', agreed_score: s.agreed_score || s.assessor_score || '',
    }])));
  };
  useEffect(() => { foundationAPI.gapReview(id).then((r) => apply(r.data)).catch((e) => setErr(jobApiError(e))); }, [id]);
  if (!sheet) return <Err text={err} />;
  const selfEdit = sheet.is_mine && sheet.status === 'SELF';
  const assessorEdit = sheet.is_assessor && sheet.status === 'ASSESSOR';
  const meetingEdit = sheet.is_assessor && sheet.status === 'MEETING';
  const set = (itemId, field, value) => setScores({ ...scores, [itemId]: { ...scores[itemId], [field]: value } });
  const num = (v) => (v ? Number(v) : null);
  const send = async (op, done) => {
    const rows = Object.entries(scores).map(([item_id, v]) => ({
      item_id: Number(item_id), self_score: num(v.self_score), self_note: v.self_note,
      assessor_score: num(v.assessor_score), assessor_note: v.assessor_note, agreed_score: num(v.agreed_score),
    }));
    try {
      const r = await foundationAPI.saveGapReview(id, { op, scores: rows, ...(op === 'meeting' ? meeting : {}) });
      apply(r.data); setErr(''); setOk(done); onChanged();
    } catch (e) { setOk(''); setErr(jobApiError(e)); }
  };
  const areas = [...new Set(sheet.scores.map((s) => s.area_label))];
  const showPlan = sheet.status === 'PLAN' || sheet.status === 'SIGNED';
  return (
    <div className="cv-subsection">
      <div className="cv-subsection-title"><span>{sheet.person} · {sheet.role_name}{sheet.area ? ` · ${sheet.area}` : ''} · {sheet.round}</span><button className="btn-secondary" onClick={onClose}>Close</button></div>
      <div style={muted}>
        {sheet.status_label}. Self by {fmtDate(sheet.self_due)} · assessor by {fmtDate(sheet.assessor_due)} · one-to-one by {fmtDate(sheet.meeting_due)} · plan by {fmtDate(sheet.plan_due)}.
        Score 1 Not yet · 2 Developing · 3 Competent · 4 Strong. This is to help you grow, not to point fingers.
      </div>
      <Err text={err} /><Ok text={ok} />
      {assessorEdit && sheet.kpi_snapshot && (
        <div style={{ ...muted, marginTop: 6 }}>
          KPI data for {sheet.kpi_snapshot.period}: {sheet.kpi_snapshot.kpis.map((k) => `${k.code} ${k.value != null ? Number(k.value) : '—'} (${(COLOURS[k.colour] || COLOURS.GREY)[1]})`).join(' · ')}
        </div>
      )}
      {sheet.meeting_held_on && <div style={{ ...muted, marginTop: 6 }}>One-to-one held {fmtDate(sheet.meeting_held_on)}{sheet.meeting_notes ? `: ${sheet.meeting_notes}` : ''}</div>}
      {areas.map((area) => (
        <div key={area} style={{ marginTop: 10 }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{area}</div>
          {sheet.scores.filter((s) => s.area_label === area).map((s) => (
            <div key={s.item_id} style={{ ...row, display: 'grid', gridTemplateColumns: '1fr 50px 130px 130px 130px 50px', gap: 8, alignItems: 'start' }}>
              <div>{s.text}
                {selfEdit
                  ? <input style={{ width: '100%', marginTop: 4 }} placeholder="Why this score (optional)" value={scores[s.item_id]?.self_note || ''} onChange={(e) => set(s.item_id, 'self_note', e.target.value)} />
                  : s.self_note && <div style={muted}>Self: {s.self_note}</div>}
                {assessorEdit
                  ? <input style={{ width: '100%', marginTop: 4 }} placeholder="Assessor's note (optional)" value={scores[s.item_id]?.assessor_note || ''} onChange={(e) => set(s.item_id, 'assessor_note', e.target.value)} />
                  : s.assessor_note && <div style={muted}>Assessor: {s.assessor_note}</div>}
              </div>
              <div style={muted}>Exp. {s.expected}</div>
              {selfEdit ? <ScoreSelect value={scores[s.item_id]?.self_score} onChange={(v) => set(s.item_id, 'self_score', v)} placeholder="Self…" /> : <div>Self: {s.self_score || '—'}</div>}
              {assessorEdit ? <ScoreSelect value={scores[s.item_id]?.assessor_score} onChange={(v) => set(s.item_id, 'assessor_score', v)} placeholder="Assessor…" /> : <div>Assessor: {s.assessor_score || '—'}</div>}
              {meetingEdit ? <ScoreSelect value={scores[s.item_id]?.agreed_score} onChange={(v) => set(s.item_id, 'agreed_score', v)} placeholder="Agreed…" /> : <div>Agreed: {s.agreed_score || '—'}</div>}
              <div style={{ fontWeight: (s.gap || 0) > 0 ? 600 : 400, color: (s.gap || 0) > 0 ? '#b45309' : undefined }}>{s.gap != null ? `Gap ${s.gap}` : ''}</div>
            </div>
          ))}
        </div>
      ))}
      {selfEdit && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
          <button className="btn-secondary" onClick={() => send('save', 'Saved.')}>Save</button>
          <button className="btn-primary" onClick={() => window.confirm('Send your self-assessment to your assessor? You cannot change it afterwards.') && send('submit', 'Sent to your assessor.')}>Submit</button>
        </div>
      )}
      {assessorEdit && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
          <button className="btn-secondary" onClick={() => send('assessor-save', 'Saved.')}>Save</button>
          <button className="btn-primary" onClick={() => window.confirm('Submit your scores? The person sees them for the one-to-one.') && send('assessor-submit', 'Scores sent. Book the one-to-one.')}>Submit scores</button>
        </div>
      )}
      {meetingEdit && (
        <div style={{ marginTop: 10 }}>
          <div style={muted}>At the one-to-one, compare the scores, agree a final score for each item and talk about why. The agreed score starts as your score.</div>
          <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
            <label style={muted}>Held on <input type="date" value={meeting.held_on} onChange={(e) => setMeeting({ ...meeting, held_on: e.target.value })} /></label>
            <input style={{ flex: 1, minWidth: 220 }} placeholder="Notes from the conversation (optional)" value={meeting.notes} onChange={(e) => setMeeting({ ...meeting, notes: e.target.value })} />
            <button className="btn-primary" onClick={() => send('meeting', 'Agreed scores saved. Now agree the development plan.')}>Save agreed scores</button>
          </div>
        </div>
      )}
      {sheet.is_mine && sheet.status === 'MEETING' && <div style={{ ...muted, marginTop: 10 }}>Your assessor has scored you. Book your one-to-one by {fmtDate(sheet.meeting_due)}.</div>}
      {showPlan && <PlanSection sheet={sheet} people={people} onChanged={(d) => { apply(d); onChanged(); }} />}
    </div>
  );
};

const AREAS = [['FUNCTIONAL', 'Functional'], ['SKILLS', 'Skills'], ['LEADERSHIP', 'Leadership'], ['ORGANISATION', 'Organisation']];

const CompanySummary = ({ round }) => {
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState({});
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');
  useEffect(() => {
    foundationAPI.gapSummary(round.id).then((r) => { setData(r.data); setSummary(r.data.summary || {}); }).catch((e) => setErr(jobApiError(e)));
  }, [round.id]);
  if (!data) return <Err text={err} />;
  const set = (area, field, value) => setSummary({ ...summary, [area]: { ...(summary[area] || {}), [field]: value } });
  const save = async () => { try { await foundationAPI.saveGapSummary(round.id, { summary }); setOk('Saved.'); setErr(''); } catch (e) { setOk(''); setErr(jobApiError(e)); } };
  return (
    <div className="cv-subsection">
      <div className="cv-subsection-title"><span>Company-level gap summary · {round.name}</span></div>
      <div style={muted}>Written by the CEO after all the one-to-ones. A gap nobody on the team can close through training becomes a skill to hire for.</div>
      <Err text={err} /><Ok text={ok} />
      {AREAS.map(([area, label]) => (
        <div key={area} style={{ ...row }}>
          <strong>{label}</strong>
          <div style={muted}>Agreed gaps: {(data.common_gaps[area] || []).map((g) => `${g.role}: ${g.item} (${g.gap})`).join(' · ') || 'none yet'}</div>
          {data.can_edit ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 4 }}>
              <textarea rows={2} placeholder="Common gaps across the team" value={summary[area]?.gaps || ''} onChange={(e) => set(area, 'gaps', e.target.value)} />
              <textarea rows={2} placeholder="Company response (group training, new hire, new process)" value={summary[area]?.response || ''} onChange={(e) => set(area, 'response', e.target.value)} />
            </div>
          ) : (
            <div>{summary[area]?.gaps || '—'}{summary[area]?.response ? ` → ${summary[area].response}` : ''}</div>
          )}
        </div>
      ))}
      {data.can_edit && <div style={{ textAlign: 'right', marginTop: 8 }}><button className="btn-primary" onClick={save}>Save summary</button></div>}
    </div>
  );
};

export const GapTab = ({ people = [] }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [summaryRound, setSummaryRound] = useState(null);
  const [f, setF] = useState({ name: 'Q4 2026', self_due: '2026-10-31', assessor_due: '2026-11-05', meeting_due: '2026-11-10', plan_due: '2026-11-15' });
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');
  const load = useCallback(() => foundationAPI.gap().then((r) => setData(r.data)).catch((e) => setErr(jobApiError(e))), []);
  useEffect(() => { load(); }, [load]);
  const openRound = async () => {
    try { const r = await foundationAPI.openGapRound(f); setOk(`Round opened: ${r.data.sheets} sheet(s), one per role holder.`); setErr(''); load(); } catch (e) { setOk(''); setErr(jobApiError(e)); }
  };
  if (!data) return <Err text={err} />;
  return (
    <div>
      <Err text={err} /><Ok text={ok} />
      {open && <Sheet key={open} id={open} people={people} onClose={() => setOpen(null)} onChanged={load} />}
      <div className="cv-subsection">
        <div className="cv-subsection-title"><span>My sheets</span></div>
        {data.mine.length === 0 && <div className="cv-subsection-empty">No sheet for you yet. Sheets are made when a round opens, one for each role you hold.</div>}
        {data.mine.map((m) => (
          <div key={m.id} style={{ ...row, display: 'flex', gap: 8, cursor: 'pointer' }} onClick={() => setOpen(m.id)}>
            <strong style={{ flex: 1 }}>{m.round} · {m.role}</strong><span style={muted}>{m.status_label} · self-assessment due {fmtDate(m.self_due)}</span>
          </div>
        ))}
      </div>
      {data.others.length > 0 && (
        <div className="cv-subsection">
          <div className="cv-subsection-title"><span>Sheets I can see</span></div>
          {data.others.map((m) => (
            <div key={m.id} style={{ ...row, display: 'flex', gap: 8, cursor: 'pointer' }} onClick={() => setOpen(m.id)}>
              <strong style={{ flex: 1 }}>{m.person} · {m.role}</strong><span style={muted}>{m.round} · {m.status_label}</span>
            </div>
          ))}
        </div>
      )}
      {data.can_see_summary && data.rounds.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '8px 0' }}>
          {data.rounds.map((r) => <button key={r.id} className="btn-secondary" onClick={() => setSummaryRound(summaryRound?.id === r.id ? null : r)}>Company summary · {r.name}</button>)}
        </div>
      )}
      {summaryRound && <CompanySummary key={summaryRound.id} round={summaryRound} />}
      {data.can_open && (
        <div className="cv-subsection">
          <div className="cv-subsection-title"><span>Open a round</span></div>
          <div style={muted}>Makes one sheet for every current role holder (link holders under Roles & stand-ins first). The Chairman role has a self-review at the yearly review instead.</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 6, marginTop: 8 }}>
            <label style={muted}>Name<input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></label>
            <label style={muted}>Self by<input type="date" value={f.self_due} onChange={(e) => setF({ ...f, self_due: e.target.value })} /></label>
            <label style={muted}>Assessor by<input type="date" value={f.assessor_due} onChange={(e) => setF({ ...f, assessor_due: e.target.value })} /></label>
            <label style={muted}>One-to-one by<input type="date" value={f.meeting_due} onChange={(e) => setF({ ...f, meeting_due: e.target.value })} /></label>
            <label style={muted}>Plans by<input type="date" value={f.plan_due} onChange={(e) => setF({ ...f, plan_due: e.target.value })} /></label>
          </div>
          <div style={{ textAlign: 'right', marginTop: 8 }}><button className="btn-primary" onClick={openRound}>Open round</button></div>
        </div>
      )}
      {data.rounds.length > 0 && <div style={muted}>Rounds: {data.rounds.map((r) => r.name).join(', ')}</div>}
    </div>
  );
};

// ─── Conduct ───────────────────────────────────────────────────────────────
const STEPS = [['REMINDER', 'Recorded reminder'], ['WRITTEN', 'Written warning'], ['FINAL', 'Final written warning'], ['DISMISSAL', 'Dismissal']];

const CaseCard = ({ c, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [step, setStep] = useState(c.suggested_step);
  const [reasons, setReasons] = useState('');
  const [action, setAction] = useState('');
  const [err, setErr] = useState('');
  const act = async (op, body) => { try { await foundationAPI.conductAction(c.id, op, body); setErr(''); setText(''); onChanged(); } catch (e) { setErr(jobApiError(e)); } };
  const d = c.deadlines;
  const pending = c.status === 'OPEN' || c.status === 'HEARING';
  return (
    <div style={row}>
      <div style={{ display: 'flex', gap: 8, cursor: 'pointer' }} onClick={() => setOpen(!open)}>
        <strong style={{ flex: 1 }}>{c.person} · Level {c.level} · section {c.rule_section} {c.rule_section_name}</strong>
        <span className="status-pill" style={{ color: pending ? '#b45309' : '#475569' }}>{c.step_label || c.status_label}</span>
      </div>
      {open && (
        <div style={{ marginTop: 6, fontSize: 13 }}>
          <Err text={err} />
          <div><strong>What happened:</strong> {c.what_happened}</div>
          <div style={muted}>Reported {fmtDate(c.reported_at)} by {c.reported_by || 'the system'}{c.source ? ` (${c.source})` : ''}. Facts due {fmtDate(d.facts_due)}{d.hearing_due ? ` · hearing due ${fmtDate(d.hearing_due)}` : ''}{d.decision_due ? ` · decision due ${fmtDate(d.decision_due)}` : ''}{d.appeal_due ? ` · appeal by ${fmtDate(d.appeal_due)}` : ''}.</div>
          {c.needs_independent_adviser && <div style={{ color: '#b45309' }}>This case involves a top role: an independent lawyer or HR consultant must take part.</div>}
          {c.facts && <div><strong>Facts:</strong> {c.facts}</div>}
          {c.person_response && <div><strong>Their side:</strong> {c.person_response}</div>}
          {c.reasons && <div><strong>Decision:</strong> {c.step_label || 'no breach'} — {c.reasons}{c.corrective_action ? ` Corrective action: ${c.corrective_action}` : ''}{c.expires_on ? ` (on record until ${fmtDate(c.expires_on)})` : ''}</div>}
          {c.appeal_text && <div><strong>Appeal:</strong> {c.appeal_text}{c.appeal_outcome ? ` — Outcome: ${c.appeal_outcome}` : ''}</div>}

          {c.is_decision_maker && pending && (
            <div style={{ marginTop: 8 }}>
              <textarea rows={2} style={{ width: '100%' }} placeholder={c.status === 'OPEN' ? 'Facts: VMS records, messages, witnesses' : 'What the person said at the hearing'} value={text} onChange={(e) => setText(e.target.value)} />
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                {c.status === 'OPEN' && <button className="btn-secondary" disabled={!text.trim()} onClick={() => act('facts', { facts: text })}>Save facts</button>}
                {c.status === 'OPEN' && c.facts && <button className="btn-secondary" onClick={() => act('notice')}>Send written notice</button>}
                {c.status === 'HEARING' && <button className="btn-secondary" disabled={!text.trim()} onClick={() => act('hearing', { person_response: text })}>Record hearing</button>}
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                <select value={step} onChange={(e) => setStep(e.target.value)}>{STEPS.map(([v, l]) => <option key={v} value={v}>{l}{v === c.suggested_step ? ' (suggested)' : ''}</option>)}</select>
                <input style={{ flex: 1, minWidth: 180 }} placeholder="Reasons, in writing" value={reasons} onChange={(e) => setReasons(e.target.value)} />
                <input style={{ flex: 1, minWidth: 160 }} placeholder="Corrective action (optional)" value={action} onChange={(e) => setAction(e.target.value)} />
                <button className="btn-primary" disabled={reasons.trim().length < 10} onClick={() => act('decide', { step, reasons, corrective_action: action })}>Decide</button>
                <button className="btn-secondary" disabled={reasons.trim().length < 10} onClick={() => act('decide', { no_breach: true, reasons })}>No breach</button>
              </div>
              {c.locked_by_legal_check && <div style={{ color: '#b45309', marginTop: 4 }}>Level 2–4 decisions wait until the Chairman records the legal check. You can gather facts and hear the person meanwhile.</div>}
            </div>
          )}
          {c.is_mine && c.status !== 'OPEN' && c.status !== 'HEARING' && (
            <div style={{ marginTop: 8 }}>
              {!c.receipt_acknowledged_at && c.step && <button className="btn-secondary" onClick={() => act('receipt')}>I have received this decision (receipt is not agreement)</button>}
              {c.status === 'DECIDED' && (
                <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                  <input style={{ flex: 1 }} placeholder="Your appeal, in writing" value={text} onChange={(e) => setText(e.target.value)} />
                  <button className="btn-secondary" disabled={text.trim().length < 10} onClick={() => act('appeal', { text })}>Appeal</button>
                </div>
              )}
            </div>
          )}
          {c.status === 'APPEALED' && !c.is_mine && !c.is_decision_maker && (
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              <input style={{ flex: 1 }} placeholder="Appeal outcome" value={text} onChange={(e) => setText(e.target.value)} />
              <button className="btn-secondary" disabled={text.trim().length < 10} onClick={() => act('appeal-decision', { outcome: text })}>Uphold</button>
              <button className="btn-secondary" disabled={text.trim().length < 10} onClick={() => act('appeal-decision', { outcome: text, overturn: true })}>Overturn</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const ConductTab = ({ people }) => {
  const [data, setData] = useState(null);
  const [f, setF] = useState({ person: '', rule_section: '', level: '1', what_happened: '' });
  const [legal, setLegal] = useState({ adviser: '', date: '' });
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');
  const load = useCallback(() => foundationAPI.conduct().then((r) => setData(r.data)).catch((e) => setErr(jobApiError(e))), []);
  useEffect(() => { load(); }, [load]);
  const run = async (fn, done) => { try { await fn(); setErr(''); setOk(done); load(); } catch (e) { setOk(''); setErr(jobApiError(e)); } };
  if (!data) return <Err text={err} />;
  const c = data.team_counts;
  return (
    <div>
      <Err text={err} /><Ok text={ok} />
      <div style={{ ...muted, marginBottom: 8 }}>Performance problems go through the gap analysis, not here. This is for breaking a team rule. Facts first, the person is always heard, the outcome is in writing, and there is a right to appeal. A mistake reported early is a learning point.</div>
      <div className="cv-subsection" style={{ borderColor: data.legal_check.done ? '#bbf7d0' : '#fed7aa' }}>
        <div className="cv-subsection-title"><span>Legal check (Part 6, section 7)</span></div>
        {data.legal_check.done
          ? <div>Done by {data.legal_check.adviser} on {fmtDate(data.legal_check.date)}. All levels can be decided.</div>
          : <div>Not done yet. Until it is, only Level 1 recorded reminders can be decided.</div>}
        {data.can_record_legal_check && !data.legal_check.done && (
          <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
            <input placeholder="Adviser's name" value={legal.adviser} onChange={(e) => setLegal({ ...legal, adviser: e.target.value })} />
            <input type="date" value={legal.date} onChange={(e) => setLegal({ ...legal, date: e.target.value })} />
            <button className="btn-secondary" disabled={!legal.adviser.trim() || !legal.date}
              onClick={() => window.confirm('Record that the labour-law adviser has checked the Consequences Framework?') && run(() => foundationAPI.legalCheck({ done: true, ...legal }), 'Legal check recorded.')}>Legal check completed</button>
          </div>
        )}
      </div>
      <div className="cv-subsection">
        <div className="cv-subsection-title"><span>Cases you can see</span><span>Team: {c.open} open · {c.active_records_by_step.REMINDER} reminder(s), {c.active_records_by_step.WRITTEN + c.active_records_by_step.FINAL} warning(s) on record</span></div>
        {data.cases.length === 0 && <div className="cv-subsection-empty">None.</div>}
        {data.cases.map((x) => <CaseCard key={x.id} c={x} onChanged={load} />)}
      </div>
      <div className="cv-subsection">
        <div className="cv-subsection-title"><span>Report a possible breach</span></div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 6 }}>
          <select value={f.person} onChange={(e) => setF({ ...f, person: e.target.value })}>
            <option value="">Who…</option>{people.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.role})</option>)}
          </select>
          <select value={f.rule_section} onChange={(e) => setF({ ...f, rule_section: e.target.value })}>
            <option value="">Team-rules section…</option>{Object.entries(data.rule_sections).map(([n, name]) => <option key={n} value={n}>{n}. {name}</option>)}
          </select>
          <select value={f.level} onChange={(e) => setF({ ...f, level: e.target.value })}>
            <option value="1">Level 1: Minor</option><option value="2">Level 2: Moderate</option><option value="3">Level 3: Serious</option><option value="4">Level 4: Gross misconduct</option>
          </select>
        </div>
        <textarea rows={2} style={{ width: '100%', marginTop: 6 }} placeholder="What happened (facts, not opinions)" value={f.what_happened} onChange={(e) => setF({ ...f, what_happened: e.target.value })} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={muted}>Forged documents or coaching a client to mislead (section 8) are always Level 4.</span>
          <button className="btn-primary" disabled={!f.person || !f.rule_section || f.what_happened.trim().length < 10}
            onClick={() => run(() => foundationAPI.reportConduct(f), 'Reported. The decision-maker has been told.').then(() => setF({ person: '', rule_section: '', level: '1', what_happened: '' }))}>Report</button>
        </div>
      </div>
    </div>
  );
};
